import { calendarInventoryDue, calendarRoundDue, calendarRoundReaders, resolveCalendarCatchup, type CalendarFacts } from './calendar-catchup.js';
import { ecId, ecLean, HandBack, record, upper, type Row } from '../ability/context.js';
import { setRowAbilityCharges } from '../ability/charges/charge-entry.js';
import { HOLDING_MASTERY } from '../ability/charges/mastery-marker.js';
import { heldRoundFrame } from './held-round-frame.js';
import { expirableStatuses, REGENERATION_ROLL_TYPE, TEMP_REGENERATION_ROLL_TYPE } from '../statuses/expiry.js';
import { statusRows, statusItemId, statusPaused, STATUS_IDS, statusCasterId, CAMPAIGN_HANDLED_STATUS_IDS } from '../statuses/expiry.js';
import { statusInputSnapshot } from './input-snapshots.js';
import { resolveEndTurnTicks, type TurnTickResult } from './ticks.js';
import { applyTurnTick, prepareTurnTick, turnTickStatusFrames } from './tick-frame.js';
import { skillsWithoutStatuses } from './status-frame.js';
import { ftCampaignTimeAddSeconds, type CampaignClockCols, type CampaignCalendarEmbed } from '../../shared/campaign-clock.js';
import type { EndTurnReaders } from './end-turn.js';
import { skipUnported, unportedSkipRow } from '../unported.js';
import { mergeSpawnPlans, spawnPlanHasEffects } from '../spawn/row-plan.js';
import { layerListEntryUuid } from '../../shared/array-types.js';
import { readItemField } from '../compile/item-field-reader.js';
import { statusEndSeconds } from '../statuses/expiry.js';
import { statusScalar } from '../actions/activity.js';
import { heldInventoryRows } from '../statuses/gm-boundary.js';

/** Observe calls, rather than property reads: native phases spread their readers. */
function observeRoundReads(read: EndTurnReaders, external: () => void): EndTurnReaders {
  if (!read.tick) return read;
  const { ability, roll } = read.tick;
  return { ...read, tick: { ...read.tick, ability: new Proxy(ability, { get(target, key) {
    const value = Reflect.get(target, key);
    if (['value', 'entries', 'has', 'statuses', 'definition', 'campaignClock'].includes(String(key)) || value === undefined) return value;
    if (typeof value === 'function') return (...args: unknown[]) => { external(); return Reflect.apply(value, target, args); };
    return value;
  } }), roll: { ...roll, d: die => { external(); return roll.d(die); }, fraction: () => { external(); return roll.fraction(); } } } };
}

const changesState = (tick: TurnTickResult): boolean => Object.keys(tick.ec).length > 0 || tick.statusesAdded.length > 0
  || tick.statusesRemoved.length > 0 || tick.statusesChanged.length > 0 || tick.charges.length > 0
  || !!tick.radiusChanges?.length || !!tick.teleportOffers?.length || !!tick.statusTriggerListeners?.length
  || !!tick.fortunateOffers?.length || spawnPlanHasEffects(tick.spawnRows) || Object.keys(tick.statusFrames ?? {}).length > 0;

/** A clock-only idle interval ends at ANY stored start/end, including caster projections.
 * Healing/light/other external readers remain round-by-round. No result or random draw is memoized. */
function idleRounds(roster: Row[], scene: Row, read: EndTurnReaders, failed: ReadonlySet<string>, remaining: number): number {
  let until = Infinity;
  const holders = new Set([scene, ...roster, ...heldInventoryRows({ encounter: scene, members: new Map(roster.map(row => [ecId(row), row])) }).values()]);
  for (const row of holders) {
    if (Number(ecLean(row, 'encounter_creature_dead')) || Number(ecLean(row, 'encounter_creature_deleted'))) continue;
    for (const mode of ['target', 'caster'] as const) for (const status of statusRows(row, mode)) {
      const paused = statusPaused(status);
      const failedExpiry = failed.has(upper(statusScalar(status, 'status_id')));
      const end = statusEndSeconds(status, read);
      if (!failedExpiry && end !== null && end > read.nowSeconds) until = Math.min(until, end);
      // A retained, already-due calendar effect (e.g. held water wet) still gets its round.
      if (!paused && !failedExpiry && mode === 'target' && end !== null && end <= read.nowSeconds && !CAMPAIGN_HANDLED_STATUS_IDS.has(statusItemId(status))) return 0;
      if (statusItemId(status) === STATUS_IDS.suffocating) return 0;
      // Native Mastery still reads paused markers when another duty runs this
      // holder's turn; pause suppresses calendar expiry, not those clock reads.
      const starts = Object.fromEntries(['year', 'month', 'day', 'hour', 'minute', 'second']
        .map(k => [k, Number(statusScalar(status, 'status_starts_campaign_' + k) ?? 0)])) as unknown as CampaignClockCols;
      if (Object.values(starts).some(n => n !== 0)) {
        const start = read.seconds(starts);
        if (start >= read.nowSeconds) until = Math.min(until, read.nowSeconds + (Math.floor((start - read.nowSeconds) / 6) + 1) * 6);
      }
    }
  }
  // Inventory clocks use a separate physical-row plan; keep that lane conservative.
  if (calendarInventoryDue(scene, roster, read)) return 0;
  return Math.min(remaining, Math.max(0, Math.ceil((until - read.nowSeconds) / 6) - 1));
}

export const emptyTurnTick = (): TurnTickResult => ({ rows: [], ec: {}, statusesAdded: [], statusesRemoved: [], statusesChanged: [], charges: [] });
/** The pending saver retains the rest of the segment. Bound CPU work as well as wire rows. */
export function pendingOocRounds(rosterSize: number, segmentRounds: number): number {
  return Math.min(16, Math.max(1, Math.floor(32768 / (64 * Math.max(1, rosterSize)))), segmentRounds);
}
/** Select work, never manufacture a skip just because a round passed. */
export function oocRoundDue(row: Row, read: EndTurnReaders): boolean {
  if (Number(ecLean(row, 'encounter_creature_dead') ?? 0) !== 0 || Number(ecLean(row, 'encounter_creature_deleted') ?? 0) !== 0) return false;
  return (['target', 'caster'] as const).some(mode => statusRows(row, mode).some(s => {
    if (statusPaused(s)) return false;
    if (mode === 'target' && [STATUS_IDS.dying, HOLDING_MASTERY].includes(statusItemId(s)) || Number(s.status_aoe_source ?? 0) !== 0) return true;
    const flags = mode === 'target' ? ['status_ends_on_target_end_of_turn', 'status_ends_on_target_start_of_turn']
      : ['status_ends_on_caster_start_of_next_turn', 'status_ends_on_caster_end_of_next_turn'];
    if (flags.some(k => Number(s[k] ?? 0) !== 0)) return true;
    if (CAMPAIGN_HANDLED_STATUS_IDS.has(statusItemId(s))) return false;
    const def = record(s.status_arr) ?? read.definition(statusItemId(s));
    if (!def) return true;
    const originId = upper(statusInputSnapshot(s).status_origin_id);
    const origin = originId ? read.definition(originId) : null;
    if (mode === 'target' && origin && [REGENERATION_ROLL_TYPE, TEMP_REGENERATION_ROLL_TYPE].includes(upper(read.value(origin, 'roll_type')))) return true;
    const layers = mode === 'target' ? ['roll_type', 'target_retries_contesting_roll_on_end_of_turn'] : ['maintained_by_ability', 'persistent_aoe'];
    return layers.some(k => {
      if (k === 'maintained_by_ability') return read.entries(def, k).some(entry => !!layerListEntryUuid(entry));
      const value = upper(read.value(def, k));
      return k === 'roll_type' ? ['77A044B7-7041-CA96-3C63-BB718B15A364', REGENERATION_ROLL_TYPE, TEMP_REGENERATION_ROLL_TYPE].includes(value) : !!value && value !== '0';
    });
  }));
}

export interface OocRoundInput { encounter: Row; rows: Row[]; rounds: number; fastForward?: boolean; clock: CampaignClockCols;
  calendar: CampaignCalendarEmbed; readers: EndTurnReaders; preflightOnly?: boolean; readersFor?: (scene: Row, clock: CampaignClockCols) => EndTurnReaders;
  calendarFacts?: CalendarFacts; weariness?: boolean;
  afterRound?: (rows: Row[], rounds: number) => Array<{ actor_id: string; item_id: string; to: number }>;
}
export type OocRoundResult = { tick: TurnTickResult; before: Record<string, Record<string, number | string | null>>; roster: Row[] } | null;

/** Synchronous action planners retain the same outcome and preflight contract. */
export function resolveOocRoundPass(input: OocRoundInput): OocRoundResult {
  const rounds = oocRounds(input);
  let step = rounds.next();
  while (!step.done) step = rounds.next();
  return step.value;
}

/** Yield without the background-tab timer clamp. */
export function yieldTimeTask(): Promise<void> {
  const scheduler = (globalThis as typeof globalThis & { scheduler?: { yield?: () => Promise<void> } }).scheduler;
  if (scheduler?.yield) return scheduler.yield();
  return new Promise(resolve => {
    const channel = new MessageChannel();
    channel.port1.onmessage = () => { channel.port1.close(); channel.port2.close(); resolve(); };
    channel.port2.postMessage(null);
  });
}

/** Pending debt and explicit pushes yield between members and calendar rounds. */
export async function resolveOocRoundPassAsync(input: OocRoundInput): Promise<OocRoundResult> {
  const rounds = oocRounds(input);
  let step = rounds.next();
  while (!step.done) {
    await yieldTimeTask();
    step = rounds.next();
  }
  return step.value;
}

/** Reuse native phases on held facts and affected writes. Each round sees prior writes. */
function* oocRounds(input: OocRoundInput): Generator<void, OocRoundResult, void> {
  const originalRead = input.readers;
  const frame = heldRoundFrame(input.encounter, new Map(input.rows.map(row => [ecId(row), row])));
  const roster = [...frame.members.values()].filter(row => !Number(ecLean(row, 'encounter_creature_deleted') ?? 0));
  const scene = frame.encounter;
  scene.encounter_creatures_arr = roster;
  const total = emptyTurnTick();
  // A retained, unsupported expiry owes one skip per pass, never another attempt each round.
  const failedExpiries = new Set<string>();
  // An idle proof belongs only to this round and ends at the next stored clock boundary.
  const idleMembers = new Set<string>();
  const initialStatuses = new Map(roster.flatMap(row => statusRows(row, 'target')).map(s => [upper(s.status_id), statusInputSnapshot(s)]));
  // CAS reads use a separate held-facts view, never the projected round writes.
  const beforeFrame = heldRoundFrame(input.encounter, new Map(input.rows.map(row => [ecId(row), row])));
  const count = input.fastForward ? 1 : input.rounds;
  const accrue = (rounds: number): void => {
    for (const charge of input.afterRound?.(roster, rounds) ?? []) {
      idleMembers.clear();
      const row = roster.find(r => ecId(r) === charge.actor_id); if (!row) continue;
      setRowAbilityCharges(row, charge.item_id, charge.to);
      const prior = total.charges.find(c => c.holder === charge.actor_id && c.item_id === charge.item_id);
      if (prior) prior.charges = charge.to; else total.charges.push({ holder: charge.actor_id, item_id: charge.item_id, charges: charge.to });
    }
  };
  const accumulate = (tick: TurnTickResult): void => {
      if (changesState(tick)) idleMembers.clear();
      if (tick.spawnRows) total.spawnRows = mergeSpawnPlans(total.spawnRows, tick.spawnRows);
      for (const [id, frame] of Object.entries(tick.statusFrames ?? {})) {
        const prior = (total.statusFrames ??= {})[id];
        total.statusFrames[id] = { ...prior, ...frame, skills: { ...prior?.skills, ...frame.skills },
          status_remove_ids: [...new Set([...(prior?.status_remove_ids ?? []), ...frame.status_remove_ids])] };
      }
      total.rows.push(...tick.rows);
      for (const change of tick.radiusChanges ?? []) {
        const prior = total.radiusChanges?.find(c => upper(c.status_id) === upper(change.status_id));
        if (prior) prior.status_aoe_distance = change.status_aoe_distance;
        else (total.radiusChanges ??= []).push({ ...change });
      }
      if (tick.statusTriggerListeners?.length) total.statusTriggerListeners = [...new Map([...(total.statusTriggerListeners ?? []), ...tick.statusTriggerListeners]
        .map(r => [JSON.stringify(r), r])).values()];
      if (tick.fortunateOffers?.length) total.fortunateOffers = [...new Set([...(total.fortunateOffers ?? []), ...tick.fortunateOffers])];
      if (tick.teleportOffers?.length) (total.teleportOffers ??= []).push(...tick.teleportOffers);
      for (const [id, changes] of Object.entries(tick.ec)) Object.assign(total.ec[id] ??= {}, changes);
      total.statusesAdded.push(...tick.statusesAdded); total.statusesRemoved.push(...tick.statusesRemoved);
      for (const change of tick.statusesChanged) {
        const prior = total.statusesChanged.find(s => s.status_id === change.status_id); if (prior) prior.status_stack = change.status_stack; else total.statusesChanged.push(change);
      }
      for (const charge of tick.charges) {
        const prior = total.charges.find(c => c.holder === charge.holder && c.item_id === charge.item_id); if (prior) prior.charges = charge.charges; else total.charges.push(charge);
      }
  };
  for (let round = 0; round < count; round++) {
    const clock = ftCampaignTimeAddSeconds(input.clock, input.calendar, input.fastForward ? input.rounds * 6 : (round + 1) * 6);
    const held = input.readersFor?.(scene, clock) ?? originalRead;
    const read: EndTurnReaders = { ...held, roster: () => roster, rowFor: id => roster.find(row => ecId(row) === upper(id)) ?? null,
      allStatusRows: () => roster.flatMap(row => [...statusRows(row, 'target'), ...statusRows(row, 'caster')]), nowSeconds: held.seconds(clock),
      tick: held.tick && { ...held.tick, ability: { ...held.tick.ability, campaignClock: () => clock } } };
    const due = roster.filter(row => oocRoundDue(row, read)).sort((a, b) => Number(ecLean(b, 'creature_initiative') ?? 0) - Number(ecLean(a, 'creature_initiative') ?? 0));
    // A zone anchored on the encounter can owe work to an otherwise plain member.
    const zones = (read.zoneStatusRows?.() ?? []).some(s => !statusPaused(s) && Number(s.status_aoe_source ?? 0) !== 0);
    const members = (zones ? roster.filter(row => !Number(ecLean(row, 'encounter_creature_dead')) && !Number(ecLean(row, 'encounter_creature_deleted'))) : due)
      .sort((a, b) => Number(ecLean(b, 'creature_initiative') ?? 0) - Number(ecLean(a, 'creature_initiative') ?? 0));
    const calendarDue = roster.some(row => calendarRoundDue(row, read, failedExpiries) || input.weariness && statusRows(row, 'target').some(s => statusItemId(s) === '5AA76DEE-CE52-E021-67CD-8C701DA0E56F'))
      || calendarInventoryDue(scene, roster, read);
    if (!members.length && !calendarDue) { accrue(count - round); break; }
    for (const row of members) {
      yield;
      if (Number(ecLean(row, 'encounter_creature_dead')) || Number(ecLean(row, 'encounter_creature_deleted'))) continue;
      const aid = ecId(row), name = String(ecLean(row, 'encounter_creature_name') ?? readItemField(row, 'item_name') ?? 'Creature');
      let idle = true;
      const expiryRead = { ...read, carriesLossTrigger: () => false, immunityExpiry: true,
        waterWetHeld: read.waterWetHeld && ((holder: Row) => { idle = false; return read.waterWetHeld!(holder); }) };
      const end = expirableStatuses([row], { kind: 'end', actorId: aid }, expiryRead);
      // The same retained-source guard as End Turn: a skipped expiry remains active for every later hook.
      const accept = (removals: typeof end.removals, prior: typeof end.removals = []): typeof end.removals => removals.filter(r => {
        const target = read.rowFor(r.target_id);
        if (!target || !skillsWithoutStatuses(target, [...prior, ...removals].filter(s => s.target_id === r.target_id).map(s => s.status_id)).netZeroWithLines.length) return true;
        end.gates.push('status-skill-net-zero'); return false;
      });
      end.removals = accept(end.removals);
      const endIds = new Set(end.removals.map(s => s.status_id));
      const start = expirableStatuses([row], { kind: 'start', actorId: aid }, expiryRead, endIds);
      start.removals = accept(start.removals, end.removals);
      const expiry = [...end.removals, ...start.removals];
      const decision = read.zoneEndOfTurn?.(aid);
      const zonePlan = decision?.kind === 'plan' ? decision : undefined;
      const skip = (reason: string): void => { total.rows.push({ ...(input.preflightOnly ? unportedSkipRow(reason)
        : skipUnported(reason, { boundary: 'ooc-round', actorId: aid })), rolls_full: `${name}: ${reason}`, roll_by: aid }); };
      if (!read.tick) { skip('ooc-round:tick-readers-unavailable'); continue; }
      let tick: TurnTickResult;
      idle &&= !decision || decision.kind === 'clear';
      const nativeRead = observeRoundReads(calendarRoundReaders(read), () => { idle = false; });
      try { tick = resolveEndTurnTicks({ encounter: scene, roster, endingId: aid, nextId: aid, nextReaction: 0,
        endRemoved: endIds, startRemoved: new Set(start.removals.map(s => s.status_id)), endExpiries: end.removals, startExpiries: start.removals,
        entries: read.abilityEntries(row), endingEntries: read.abilityEntries(row), skipStart: true, outOfCombat: true, zones: zonePlan, preflightOnly: input.preflightOnly,
        read: nativeRead.tick!, prepare: result => {
          if (new Set([...(total.statusTriggerListeners ?? []), ...(result.statusTriggerListeners ?? [])].map(r => JSON.stringify(r))).size > 128)
            throw new HandBack('janus_trigger:listener-receipt-capacity');
          prepareTurnTick(roster, result, read.abilityEntries);
        } }); }
      catch (error) {
        if (error instanceof HandBack && error.reason.includes('trigger-consent')) throw error;
        skip(error instanceof HandBack ? error.reason : 'ooc-round:exception:' + String((error as Error)?.message ?? error));
        continue;
      }
      // The turn lane normally persists plain expiries alongside the tick. The time lane carries all of them inside it.
      for (const removal of Number(tick.ec[aid]?.encounter_creature_dead) ? [] : expiry) {
        // Departed holders use the tick's exact raw tombstone, with no target frame.
        if (!read.rowFor(removal.target_id)) continue;
        const status = initialStatuses.get(removal.status_id) ?? roster.flatMap(r => statusRows(r, 'target')).find(s => upper(s.status_id) === removal.status_id);
        if (!status || tick.statusesRemoved.some(s => s.status_id === removal.status_id)) continue;
        tick.statusesRemoved.push({ status_item_id: removal.item_id, target: removal.target_id, caster: statusCasterId(status), status_id: removal.status_id, before: statusInputSnapshot(status) });
        if (removal.defenses !== undefined || removal.attack_budget) (tick.statusFrames ??= {})[removal.target_id] = {
          ...tick.statusFrames?.[removal.target_id], status_remove_ids: [], skills: {},
          ...(removal.defenses !== undefined ? { defenses: removal.defenses } : {}),
          ...(removal.attack_budget ? { attack_budget: removal.attack_budget } : {}) };
      }
      tick.statusesAdded = tick.statusesAdded.map(s => ({ ...s, status_id: read.uuid() }));
      tick.rows = tick.rows.filter(r => r.roll_type_1 !== 'end_turn' && r.roll_type_1 !== 'start_turn');
      if (idle && !changesState(tick) && tick.rows.every(r => r.roll_type_1 === 'janus_unported_skip')) idleMembers.add(aid);
      for (const log of tick.rows) if (log.roll_type_1 === 'janus_unported_skip' && !String(log.rolls_full).startsWith(name + ':')) log.rolls_full = `${name}: ${String(log.rolls_full)}`;
      tick.statusFrames = turnTickStatusFrames(roster, tick);
      applyTurnTick(roster, tick, read.definition, scene);
      accumulate(tick);
      if (decision?.kind === 'handback') skip(`persistent-aoe-present:${decision.reason}`);
      for (const reason of [...new Set([...end.gates, ...start.gates])].filter(r => !['dot-tick', 'contest-retry', 'dying-actor', 'maintained-expiry', 'regeneration-source', 'expiry-detonation', 'out-of-breath-status'].includes(r))) skip(reason);
    }
    let calendarIdle = true;
    if (read.tick) {
      yield;
      const calendarTick = resolveCalendarCatchup({ encounter: scene, roster, clock, calendar: input.calendar,
        failedExpiries,
        consumedStatusIds: new Set((total.spawnRows?.writes ?? []).filter(w => w.table === 'statuses' && w.row.status_deleted === 1).map(w => upper(w.row.status_id))),
        read: observeRoundReads(read, () => { calendarIdle = false; }), facts: input.calendarFacts && { ...input.calendarFacts, awakeNotices: [...input.calendarFacts.awakeNotices, ...total.rows.filter(r => r.roll_type_1 === 'janus_unported_skip').map(r => upper(r.roll_type_2))] }, preflightOnly: input.preflightOnly, weariness: input.weariness });
      if (changesState(calendarTick) || calendarTick.rows.some(r => r.roll_type_1 !== 'janus_unported_skip')) calendarIdle = false;
      applyTurnTick(roster, calendarTick, read.definition, scene);
      accumulate(calendarTick);
    }
    accrue(1);
    if (!zones && calendarIdle && members.every(row => idleMembers.has(ecId(row))) && !input.fastForward) {
      // Clock-dependent proofs expire at the next event, including a future Mastery stamp.
      const skipped = idleRounds(roster, scene, read, failedExpiries, count - round - 1);
      if (skipped) { accrue(skipped); round += skipped; }
    }
    idleMembers.clear();
    if (round + 1 < count) yield;
  }
  if (!total.rows.length && !Object.keys(total.ec).length && !total.statusesRemoved.length && !total.statusesAdded.length
    && !total.statusesChanged.length && !total.charges.length && !total.radiusChanges?.length && !total.teleportOffers?.length && !spawnPlanHasEffects(total.spawnRows)) return null;
  // An intermediate new row can expire again during this span; it never existed in persisted state.
  const transient = new Set(total.statusesAdded.map(s => upper((s as unknown as Row).status_id)).filter(id => total.statusesRemoved.some(s => s.status_id === id)));
  total.statusesAdded = total.statusesAdded.filter(s => !transient.has(upper((s as unknown as Row).status_id)));
  total.statusesRemoved = total.statusesRemoved.filter(s => !transient.has(upper(s.status_id)));
  for (const change of total.statusesChanged) {
    const added = total.statusesAdded.find(s => upper((s as unknown as Row).status_id) === upper(change.status_id));
    if (added) added.status_stack = change.status_stack;
  }
  const removed = new Set(total.statusesRemoved.map(s => upper(s.status_id)));
  total.statusesChanged = total.statusesChanged.filter(s => initialStatuses.has(upper(s.status_id)) && !removed.has(upper(s.status_id)));
  const skipKeys = new Set<string>();
  total.rows = total.rows.filter(row => {
    if (row.roll_type_1 !== 'janus_unported_skip') return true;
    const key = upper(row.roll_by) + '|' + row.rolls_full;
    if (skipKeys.has(key)) return false; skipKeys.add(key); return true;
  });
  for (const removal of total.statusesRemoved) {
    const original = initialStatuses.get(upper(removal.status_id));
    if (original) removal.before = statusInputSnapshot(original);
  }
  const prepared = prepareTurnTick([...beforeFrame.members.values()], total, originalRead.abilityEntries);
  total.charges = prepared.charges;
  return { tick: total, before: prepared.before, roster: roster.map(row => frame.finish(row)) };
}
